import { useEffect, useState } from "react";
import { getBusinessQuestions } from "../api/diagnosticsApi";

export default function BusinessForm({ onSubmit }: { onSubmit: (answers: any) => void }) {
  const [questions, setQuestions] = useState<any[]>([]);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const questionsPerPage = 4;

  useEffect(() => {
    getBusinessQuestions().then(setQuestions);
  }, []);

  function handleChange(id: string, value: string) {
    setAnswers(prev => ({ ...prev, [id]: value }));
  }

  function nextQuestion() {
    if (currentIndex + questionsPerPage < questions.length) {
      setCurrentIndex(prev => prev + questionsPerPage);
    }
  }

  function prevQuestion() {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - questionsPerPage);
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(answers);
  }

  if (questions.length === 0) return <p>Loading questions...</p>;

  const currentQuestions = questions.slice(currentIndex, currentIndex + questionsPerPage);
  const isLastPage = currentIndex + questionsPerPage >= questions.length;
  const isCurrentSetComplete = currentQuestions.every(q => {
    const val = answers[q.id];
    if (q.type === 'slider') return true; // sliders usually have a default
    return (val || "").trim() !== "";
  });

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {currentQuestions.map((q, idx) => {
          const currentAnswer = answers[q.id] || "";
          return (
            <div key={q.id} className="card p-6 border rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: `${idx * 100}ms` }}>
              <label className="block text-lg font-semibold text-slate-900 mb-3">{q.label}</label>

              {q.type === "select" && q.options ? (
                <select
                  value={currentAnswer}
                  onChange={e => handleChange(q.id, e.target.value)}
                  className="input-base px-3 focus:ring-offset-0"
                  required
                >
                  <option value="" disabled>{q.placeholder}</option>
                  {q.options.map((opt: string) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : q.type === "textarea" ? (
                <textarea
                  placeholder={q.placeholder}
                  value={currentAnswer}
                  onChange={e => handleChange(q.id, e.target.value)}
                  className="input-base px-3 focus:ring-offset-0"
                  required
                />
              ) : q.type === "slider" ? (
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={currentAnswer || "50"}
                  onChange={e => handleChange(q.id, e.target.value)}
                  className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                />
              ) : (
                <input
                  type={q.type}
                  placeholder={q.placeholder}
                  value={currentAnswer}
                  onChange={e => handleChange(q.id, e.target.value)}
                  className="input-base px-3 focus:ring-offset-0"
                  required
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex justify-between pt-4">
        <button
          type="button"
          onClick={prevQuestion}
          disabled={currentIndex === 0}
          className="btn-secondary btn-md shrink-0"
        >
          Previous
        </button>

        {!isLastPage ? (
          <button
            type="button"
            onClick={nextQuestion}
            disabled={!isCurrentSetComplete}
            className="btn-primary btn-md shrink-0"
          >
            Next
          </button>
        ) : (
          <button
            type="submit"
            disabled={!isCurrentSetComplete}
            className="btn-primary btn-md shrink-0"
          >
            Submit
          </button>
        )}
      </div>

      <p className="text-sm text-slate-500 text-center">
        Showing {currentIndex + 1} - {Math.min(currentIndex + questionsPerPage, questions.length)} of {questions.length}
      </p>
    </form>
  );
}
